import React, { useState } from "react"
import { Link } from "react-router-dom"
import useExperiments from "../../hooks/useExperiments.js"

const FILTERS = [
  "all",
  "running",
  "pending_approval",
  "paused",
  "completed",
  "draft",
  "archived"
]

const STATUS_PILL = {
  draft:             { bg: "#F8FAFC", fg: "#64748B" },
  pending_approval:  { bg: "#ECFDF5", fg: "#047857" },
  running:           { bg: "#F1F5F9", fg: "#475569" },
  paused:            { bg: "#F8FAFC", fg: "#475569" },
  completed:         { bg: "#D1FAE5", fg: "#047857" },
  archived:          { bg: "#F8FAFC", fg: "#64748B" }
}

const MONTHS = [ "JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC" ]

function formatDate(iso) {
  if (!iso) return ""
  const d = new Date(iso)
  return `${MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}`
}

// M9/9g — render scope-aware target meta. Single-page experiments show
// the exact path; page-type show "All {type} pages · {pattern}" so the
// owner understands the experiment applies to every matching path.
function scopeLabel(t, experiment) {
  if (experiment.scope === "page_type") {
    return t("dashboard.experiments.scope_page_type", {
      page_type: experiment.page_type,
      pattern: experiment.page_pattern
    })
  }
  return experiment.page_path || experiment.page_pattern || ""
}

function conversionRate(experiment) {
  const { impressions_count, conversions_count } = experiment
  if (!impressions_count) return null
  return (conversions_count / impressions_count) * 100
}

function FilterTabs({ t, counts, active, onChange }) {
  return (
    <nav
      className="flex flex-wrap gap-1 border-b border-rule"
      role="tablist"
    >
      {FILTERS.map((key) => {
        const isActive = key === active
        const count = counts[key] || 0
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(key)}
            className="font-mono text-[11px] font-medium px-3 py-2 -mb-px"
            style={{
              color: isActive ? "#0F172A" : "#64748B",
              borderBottom: isActive ? "1px solid #0F172A" : "1px solid transparent",
              letterSpacing: "0.08em",
              fontVariantNumeric: "tabular-nums"
            }}
          >
            {t(`dashboard.experiments.filter_${key}`)}
            <span
              className="ml-2 text-[10px]"
              style={{ color: isActive ? "#475569" : "#94A3B8" }}
            >
              {count}
            </span>
          </button>
        )
      })}
    </nav>
  )
}

function ExperimentRow({ t, experiment }) {
  const pill = STATUS_PILL[experiment.status] || STATUS_PILL.archived
  const rate = conversionRate(experiment)
  const dateIso = experiment.completed_at || experiment.started_at || experiment.created_at

  return (
    <li
      className="grid gap-5 items-center border-b border-rule last:border-b-0"
      style={{ gridTemplateColumns: "120px minmax(0,1fr) 90px 60px" }}
    >
      <Link
        to={`/experiments/${experiment.id}`}
        className="contents group"
      >
        <span
          className="inline-flex items-center gap-2 text-[11px] font-medium font-mono rounded-pill my-4"
          style={{ background: pill.bg, color: pill.fg, padding: "3px 10px", width: "fit-content" }}
        >
          <span className="w-[6px] h-[6px] rounded-full" style={{ background: pill.fg, opacity: 0.55 }} />
          {t(`dashboard.experiments.status_${experiment.status}`)}
        </span>

        <div className="min-w-0 py-4">
          <div className="text-sm text-ink-primary truncate group-hover:text-ai">
            {experiment.name}
          </div>
          <div
            className="font-mono text-[11px] text-ink-faint truncate"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {experiment.target_selector} · {scopeLabel(t, experiment)}
          </div>
        </div>

        <span
          className="text-[15px] font-medium text-ink-primary text-right py-4"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {rate == null ? "—" : `${rate.toFixed(1)}%`}
        </span>
        <span
          className="font-mono text-[11px] text-ink-tertiary text-right py-4"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {formatDate(dateIso)}
        </span>
      </Link>
    </li>
  )
}

export default function ExperimentsPage({ t }) {
  const { status, data: experiments, error } = useExperiments()
  const [filter, setFilter] = useState("all")

  const counts = experiments.reduce(
    (acc, e) => {
      acc.all += 1
      acc[e.status] = (acc[e.status] || 0) + 1
      return acc
    },
    { all: 0 }
  )

  const visible = filter === "all"
    ? experiments
    : experiments.filter((e) => e.status === filter)

  return (
    <div className="space-y-10">
      <header>
        <p
          className="font-mono text-[10px] text-ink-tertiary mb-3"
          style={{ letterSpacing: "0.15em", fontVariantNumeric: "tabular-nums" }}
        >
          {t("dashboard.experiments.eyebrow")}
        </p>
        <h1
          className="text-[28px] font-medium text-ink-primary"
          style={{ letterSpacing: "-0.035em" }}
        >
          {t("dashboard.experiments.title")}
        </h1>
        <p className="mt-3 text-sm text-ink-secondary max-w-2xl" style={{ lineHeight: 1.55 }}>
          {t("dashboard.experiments.lede")}
        </p>
      </header>

      <FilterTabs t={t} counts={counts} active={filter} onChange={setFilter} />

      {status === "loading" && (
        <p className="text-sm text-ink-tertiary">{t("dashboard.experiments.loading")}</p>
      )}
      {status === "error" && (
        <p className="text-sm text-failure">{error || t("dashboard.experiments.error")}</p>
      )}
      {status === "loaded" && visible.length === 0 && (
        <p className="text-sm text-ink-tertiary py-4">
          {filter === "all"
            ? t("dashboard.experiments.empty_all")
            : t("dashboard.experiments.empty_filter")}
        </p>
      )}
      {status === "loaded" && visible.length > 0 && (
        <ul className="border-t" style={{ borderTopWidth: 1, borderTopColor: "#0F172A" }}>
          {visible.map((e) => <ExperimentRow key={e.id} t={t} experiment={e} />)}
        </ul>
      )}
    </div>
  )
}
